import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { ChipColour } from '../../../../chip/chip.component';
import { DryEyeFormGroup } from '../../dry-eye-form/dry-eye-form.model';
import { DEWS_MEDICAL_HISTORY_FIELDS } from '../dews-iii-medical-history/dews-iii-medical-history.schema';
import { getIdentifiedDrivers } from '../dews-iii-drivers/dews-iii-drivers.component';
import { DewsIIIService, discordanceTypeLabel, HOMEOSTASIS_CATEGORY_LABELS, HomeostasisResult, SymptomClassification } from '../dews-iii.service';

interface HomeostasisResultDisplay {
  label: string;
  resultLabel: string;
  resultClass: string;
  chipColour: ChipColour;
}

@Component({
  selector: 'csi-dews-iii-summary',
  templateUrl: 'dews-iii-summary.component.html',
  styleUrls: ['dews-iii-summary.component.scss']
})
export class DewsIIISummaryComponent implements OnInit, OnDestroy {
  @Input() formGroup!: DryEyeFormGroup;

  diagnosisBannerClass = 'banner-ruled-out';
  diagnosisLabel = 'DED Not Diagnosed';
  osdiScore: number | null = null;
  symptomClassificationLabel = 'No OSDI-6 Score';
  homeostasisResultDisplays: HomeostasisResultDisplay[] = [];
  homeostasisResultsEmpty = true;
  discordanceType: string = null;
  discordanceLabel = '';
  showDiscordanceSection = false;
  medicalHistoryFlags: string[] = [];
  identifiedDrivers: string[] = [];
  driverNotes = '';
  dedDiagnosed = false;
  effectiveDedDiagnosed = false;
  clinicianOverride = false;
  clinicianOverrideNotes = '';

  constructor(private dewsIIIService: DewsIIIService) {}

  ngOnInit() {
    this.updateSummary();
    this.formGroup.valueChanges.pipe(untilDestroyed(this)).subscribe(() => this.updateSummary());
  }

  ngOnDestroy() {}

  private updateSummary() {
    this.osdiScore = this.computeOsdiScore();
    this.symptomClassificationLabel = this.computeSymptomClassificationLabel();
    this.dedDiagnosed = this.computeDedDiagnosed();
    this.clinicianOverride = this.computeClinicianOverride();
    this.effectiveDedDiagnosed = this.computeEffectiveDedDiagnosed();
    this.discordanceType = this.computeDiscordanceType();
    this.discordanceLabel = this.computeDiscordanceLabel();
    this.showDiscordanceSection =
      this.discordanceType !== null && this.discordanceType !== 'neither';
    this.diagnosisLabel = this.computeDiagnosisLabel();
    this.diagnosisBannerClass = this.computeDiagnosisBannerClass();
    this.homeostasisResultDisplays = this.computeHomeostasisResultDisplays();
    this.homeostasisResultsEmpty = this.homeostasisResultDisplays.length === 0;
    this.medicalHistoryFlags = this.computeMedicalHistoryFlags();
    this.identifiedDrivers = this.computeIdentifiedDrivers();
    this.driverNotes = this.computeDriverNotes();
    this.clinicianOverrideNotes = this.computeClinicianOverrideNotes();
  }

  private computeOsdiScore(): number {
    const control = this.formGroup.controls['osdi6'];
    if (control && control.value !== null && control.value !== undefined) {
      return control.value;
    }
    return null;
  }

  private computeSymptomClassificationLabel(): string {
    if (this.osdiScore === null || this.osdiScore === undefined) {
      return 'No OSDI-6 Score';
    }
    const classification: SymptomClassification = this.dewsIIIService.classifyOsdiScore(
      this.osdiScore
    );
    switch (classification) {
      case 'normal':
        return 'Normal';
      case 'mildToModerate':
        return 'Mild to Moderate';
      case 'severe':
        return 'Severe';
      default:
        return 'Unknown';
    }
  }

  private computeDedDiagnosed(): boolean {
    const control = this.formGroup.controls['dewsDedDiagnosed'];
    return control && control.value === true;
  }

  private computeDiscordanceType(): string {
    const control = this.formGroup.controls['dewsDiscordanceType'];
    if (!control || !control.value) {
      return null;
    }
    return control.value;
  }

  private computeDiscordanceLabel(): string {
    return discordanceTypeLabel(this.discordanceType);
  }

  private computeEffectiveDedDiagnosed(): boolean {
    if (this.clinicianOverride) {
      return !this.dedDiagnosed;
    }
    return this.dedDiagnosed;
  }

  private computeDiagnosisLabel(): string {
    if (this.clinicianOverride) {
      return 'DED Diagnosed (Clinician Override)';
    }
    if (this.dedDiagnosed) {
      return 'DED Diagnosed';
    }
    if (this.discordanceType && this.discordanceType !== 'neither') {
      return 'Discordance: ' + this.discordanceLabel;
    }
    return 'DED Not Diagnosed';
  }

  private computeDiagnosisBannerClass(): string {
    if (this.clinicianOverride) {
      return this.effectiveDedDiagnosed ? 'banner-diagnosed' : 'banner-ruled-out';
    }
    if (this.dedDiagnosed) {
      return 'banner-diagnosed';
    }
    if (this.discordanceType && this.discordanceType !== 'neither') {
      return 'banner-discordance';
    }
    return 'banner-ruled-out';
  }

  private computeHomeostasisResultDisplays(): HomeostasisResultDisplay[] {
    const control = this.formGroup.controls['dewsHomeostasisResults'];
    const results: HomeostasisResult[] =
      control && control.value && Array.isArray(control.value) ? control.value : [];

    return results.map(result => ({
      label: this.getCategoryLabel(result.category),
      resultLabel: result.performed ? (result.positive ? 'Positive' : 'Negative') : 'Not Performed',
      resultClass: result.performed
        ? result.positive
          ? 'result-positive'
          : 'result-negative'
        : 'result-not-performed',
      chipColour: result.performed
        ? result.positive
          ? ('red-no-outline' as ChipColour)
          : ('green-no-outline' as ChipColour)
        : ('grey-no-outline' as ChipColour)
    }));
  }

  private getCategoryLabel(category: string): string {
    const meta = HOMEOSTASIS_CATEGORY_LABELS[category];
    return meta ? meta.label : category;
  }

  private computeMedicalHistoryFlags(): string[] {
    const controls = this.formGroup.controls;
    return DEWS_MEDICAL_HISTORY_FIELDS
      .filter(f => controls[f.key] && controls[f.key].value === f.expected)
      .map(f => f.flagLabel);
  }

  private computeIdentifiedDrivers(): string[] {
    return getIdentifiedDrivers(this.formGroup.controls);
  }

  private computeDriverNotes(): string {
    const control = this.formGroup.controls['dewsDriverNotes'];
    return control && control.value ? control.value : '';
  }

  private computeClinicianOverride(): boolean {
    const control = this.formGroup.controls['dewsClinicianOverride'];
    return control && control.value === true;
  }

  private computeClinicianOverrideNotes(): string {
    const control = this.formGroup.controls['dewsDiagnosisNotes'];
    return control && control.value ? control.value : '';
  }
}
